/**
 * Remote players, rendered.
 *
 * The problem: snapshots arrive at 15 Hz, jittered, and the game renders at 144.
 * The fix is the standard one — **render everyone 130 ms in the past** and
 * interpolate between the two snapshots that bracket that moment. The buffer is
 * keyed off *local receive time*, never the server's `ts`, so there is no clock
 * sync to get wrong and a laggy server degrades into smooth-but-late instead of
 * jittery-and-current.
 *
 * When the next snapshot is late we extrapolate along the last measured velocity
 * for at most 250 ms, then freeze. Past two seconds of silence the ghost fades to
 * 30% — a lie you can see is better than a lie you cannot.
 *
 * `InterpBuffer` is deliberately free of three.js and of the DOM: it is the only
 * part with maths worth getting wrong, so it is unit-tested headlessly in
 * `ghosts.selftest.ts`.
 */

import { Scene, Vector3 } from 'three';
import {
  ANIM_AIRBORNE,
  ANIM_BOUNCE,
  ANIM_FLAIL,
  type AnimState,
  type PlayerInfo,
  type PlayerState,
  type S2CJoined,
  type S2CLeft,
  type S2CSnap,
  type S2CWelcome,
} from '@shared/protocol';
import { EXTRAPOLATE_MAX_MS, INTERP_DELAY_MS, SNAPSHOT_BUFFER_MS } from '@shared/constants';
import type { SkeletonPose, SkeletonRig } from '../render/rigTypes';
import { createFallbackRig } from '../render/ghostFallbackRig';
import { createNameTag, type NameTag } from '../render/nametag';
import { stickUpFromLean } from '../physics/pogo';

// ---------------------------------------------------------------------------
// tuning
// ---------------------------------------------------------------------------

/** Hard ceiling on extrapolated speed, so one corrupt snapshot cannot fling a ghost. */
const MAX_EXTRAP_SPEED = 40; // m/s

/** Silence beyond this fades the ghost out to `STALE_OPACITY`. */
const STALE_MS = 2000;
const STALE_OPACITY = 0.3;

/** How many ghosts get the full skeleton. Everyone else is a capsule. */
const LOD_FULL_COUNT = 12;

/** Name tags: fully opaque within `TAG_FADE_NEAR`, gone by `TAG_FADE_FAR`. */
const TAG_FADE_NEAR = 80;
const TAG_FADE_FAR = 120;
const TAG_HEIGHT = 2.2;

/** Impact compression pulse: 1 at landing, decaying at this rate per second. */
const COMPRESSION_DECAY = 6;

/** Absolute guard on buffer length, in case a server ever floods us. */
const MAX_SAMPLES = 64;

const TAU = Math.PI * 2;

// ---------------------------------------------------------------------------
// pure interpolation core
// ---------------------------------------------------------------------------

/**
 * How a sample was produced.
 *
 * - `interp` — between two real snapshots. The happy path.
 * - `extrap` — past the newest snapshot, guessing along the last velocity.
 * - `hold`   — frozen: either before the oldest snapshot (buffer just opened) or
 *              past the extrapolation limit.
 */
export type SampleMode = 'interp' | 'extrap' | 'hold';

export interface SampledState {
  state: PlayerState;
  mode: SampleMode;
  /** How far the render time is past the newest snapshot, in ms. `0` while interpolating. */
  staleMs: number;
}

/** Shortest-arc angle lerp. `lerpAngle(3.0, -3.0, 1)` goes *up* through π, not down through 0. */
export function lerpAngle(a: number, b: number, u: number): number {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  else if (d < -Math.PI) d += TAU;
  return a + d * u;
}

function lerp(a: number, b: number, u: number): number {
  return a + (b - a) * u;
}

interface Sample {
  /** Local receive time in ms (`performance.now()`), never the server clock. */
  t: number;
  s: PlayerState;
}

/**
 * A ring of recent snapshots for one player, plus the maths to read a pose out of
 * it at an arbitrary moment.
 *
 * Pure data: no three.js, no DOM, no clock of its own — every method takes the
 * time it should use. That is what makes it testable.
 */
export class InterpBuffer {
  private readonly samples: Sample[] = [];

  /** Number of retained snapshots. Diagnostics and tests. */
  get size(): number {
    return this.samples.length;
  }

  /** Local receive time of the newest snapshot, or `-Infinity` when empty. */
  get newestTime(): number {
    const last = this.samples[this.samples.length - 1];
    return last === undefined ? -Infinity : last.t;
  }

  /** True once at least one snapshot has landed. */
  get hasData(): boolean {
    return this.samples.length > 0;
  }

  /**
   * Record a snapshot. `tLocal` is normally monotonic; out-of-order arrivals are
   * inserted in the right place rather than dropped, because a late packet still
   * carries real information.
   */
  push(tLocal: number, s: PlayerState): void {
    const n = this.samples.length;
    const last = this.samples[n - 1];
    if (last === undefined || tLocal >= last.t) {
      this.samples.push({ t: tLocal, s });
    } else {
      let i = n - 1;
      while (i > 0) {
        const prev = this.samples[i - 1];
        if (prev !== undefined && prev.t <= tLocal) break;
        i--;
      }
      this.samples.splice(i, 0, { t: tLocal, s });
    }
    if (this.samples.length > MAX_SAMPLES) this.samples.shift();
  }

  /**
   * Drop snapshots more than `SNAPSHOT_BUFFER_MS` behind `renderTime`.
   *
   * Always keeps the newest **two**, not one: the second-newest is what
   * `extrapolate` measures velocity from, and pruning it mid-freeze would snap a
   * held ghost back to its last known position — a visible pop exactly when the
   * connection is at its worst.
   */
  prune(renderTime: number): void {
    const cutoff = renderTime - SNAPSHOT_BUFFER_MS;
    while (this.samples.length > 2) {
      const first = this.samples[0];
      if (first === undefined || first.t >= cutoff) break;
      this.samples.shift();
    }
  }

  /**
   * Read the state at `renderTime` (already delayed by `INTERP_DELAY_MS` by the
   * caller). Returns `null` only while the buffer is empty.
   *
   * Prunes as a side effect — sampling is the only thing that knows what "now"
   * is, so it is also the only thing that can tell which history is dead.
   */
  sample(renderTime: number): SampledState | null {
    this.prune(renderTime);
    const n = this.samples.length;
    if (n === 0) return null;

    const first = this.samples[0];
    const last = this.samples[n - 1];
    if (first === undefined || last === undefined) return null;

    // Before the buffer opens: hold the oldest sample rather than invent history.
    if (renderTime <= first.t) {
      return { state: cloneState(first.s), mode: 'hold', staleMs: 0 };
    }

    if (renderTime >= last.t) return this.extrapolate(renderTime, last);

    // Bracketing pair. The buffer holds ~15 entries, so a backwards scan beats
    // the cache miss of a binary search.
    for (let i = n - 2; i >= 0; i--) {
      const a = this.samples[i];
      const b = this.samples[i + 1];
      if (a === undefined || b === undefined) continue;
      if (a.t > renderTime) continue;
      const span = b.t - a.t;
      const u = span > 0 ? (renderTime - a.t) / span : 1;
      return { state: mixState(a.s, b.s, u), mode: 'interp', staleMs: 0 };
    }

    // Unreachable given the guards above, but a pose beats a crash.
    return { state: cloneState(first.s), mode: 'hold', staleMs: 0 };
  }

  private extrapolate(renderTime: number, last: Sample): SampledState {
    const staleMs = renderTime - last.t;
    const mode: SampleMode = staleMs <= EXTRAPOLATE_MAX_MS ? 'extrap' : 'hold';
    // Past the limit we do not keep guessing — we freeze at the last guess.
    const dt = Math.min(staleMs, EXTRAPOLATE_MAX_MS);

    const state = cloneState(last.s);
    if (dt <= 0) return { state, mode, staleMs };

    const prev = this.samples[this.samples.length - 2];
    if (prev === undefined) return { state, mode, staleMs };
    const span = last.t - prev.t;
    if (span <= 0) return { state, mode, staleMs };

    // Velocity in metres per millisecond, clamped so a garbage snapshot pair
    // cannot launch a ghost across the level.
    let vx = (last.s.p[0] - prev.s.p[0]) / span;
    let vy = (last.s.p[1] - prev.s.p[1]) / span;
    let vz = (last.s.p[2] - prev.s.p[2]) / span;
    const speed = Math.hypot(vx, vy, vz) * 1000;
    if (speed > MAX_EXTRAP_SPEED) {
      const k = MAX_EXTRAP_SPEED / speed;
      vx *= k;
      vy *= k;
      vz *= k;
    }

    state.p[0] = last.s.p[0] + vx * dt;
    state.p[1] = last.s.p[1] + vy * dt;
    state.p[2] = last.s.p[2] + vz * dt;
    return { state, mode, staleMs };
  }
}

function cloneState(s: PlayerState): PlayerState {
  return {
    p: [s.p[0], s.p[1], s.p[2]],
    l: [s.l[0], s.l[1]],
    y: s.y,
    a: s.a,
    c: s.c,
    v: s.v,
  };
}

/** Continuous fields lerp; `anim` is discrete and comes from the newer sample. */
function mixState(a: PlayerState, b: PlayerState, u: number): PlayerState {
  return {
    p: [lerp(a.p[0], b.p[0], u), lerp(a.p[1], b.p[1], u), lerp(a.p[2], b.p[2], u)],
    l: [lerp(a.l[0], b.l[0], u), lerp(a.l[1], b.l[1], u)],
    y: lerpAngle(a.y, b.y, u),
    a: b.a,
    c: lerp(a.c, b.c, u),
    v: lerp(a.v, b.v, u),
  };
}

// ---------------------------------------------------------------------------
// ghost bookkeeping
// ---------------------------------------------------------------------------

/** Chunk B's `createSkeleton`. Until integration, `createFallbackRig` stands in. */
export type RigFactory = (tint: number) => SkeletonRig;

/** One row of the HUD leaderboard. */
export interface RosterEntry {
  id: number;
  name: string;
  color: number;
  heightM: number;
}

interface Ghost {
  info: PlayerInfo;
  buf: InterpBuffer;
  /** Built the first time this ghost is close enough to deserve a skeleton. */
  rig: SkeletonRig | null;
  fallback: SkeletonRig;
  tag: NameTag;
  pose: SkeletonPose;
  /** Reused so `applyPose` never allocates. */
  position: Vector3;
  stickDir: Vector3;
  /** False until the first snapshot lands — an unposed ghost stays hidden. */
  posed: boolean;
  prevAnim: AnimState;
  compression: number;
  opacity: number;
  /** Last opacity actually pushed to each rig, so we do not touch materials per frame. */
  rigOpacity: number;
  fallbackOpacity: number;
  tagOpacity: number;
  dist: number;
  heightM: number;
  full: boolean;
}

/**
 * Every remote player, from `welcome` to `left`.
 *
 * Owns the rigs, the name tags and the interpolation buffers; owns nothing about
 * the local player. Feed it the four network events and call `update` once per
 * rendered frame.
 */
export class GhostManager {
  private readonly ghosts = new Map<number, Ghost>();
  /** Scratch list reused by `update`'s LOD sort — allocation-free per frame. */
  private readonly order: Ghost[] = [];
  private selfId: number | null = null;
  private lastNow = -1;

  constructor(
    private readonly scene: Scene,
    private readonly createRig: RigFactory,
  ) {}

  // -------------------------------------------------------------------------
  // network events
  // -------------------------------------------------------------------------

  /**
   * Rebuild the room from a handshake. Clears any existing ghosts first, so a
   * reconnect cannot leave duplicates behind.
   */
  handleWelcome(msg: S2CWelcome): void {
    this.clearGhosts();
    this.selfId = msg.id;
    const now = performance.now();
    for (const p of msg.players) {
      if (p.id === msg.id) continue;
      const ghost = this.spawn({ id: p.id, name: p.name, color: p.color });
      // Defensive: the handshake is network data, and a player with no state yet
      // should simply stay hidden until their first snapshot.
      if (p.s !== undefined && p.s !== null) ghost.buf.push(now, cloneState(p.s));
    }
  }

  /**
   * Somebody joined. Accepts either the raw `joined` message or the bare
   * `PlayerInfo` that `NetClient` emits, because both spellings are in the wild.
   */
  handleJoined(msg: S2CJoined | PlayerInfo): void {
    const info = 't' in msg ? msg.player : msg;
    if (info.id === this.selfId) return;
    if (this.ghosts.has(info.id)) return;
    this.spawn(info);
  }

  /** Somebody left. Accepts the raw message or a bare id. */
  handleLeft(msg: S2CLeft | number): void {
    const id = typeof msg === 'number' ? msg : msg.id;
    const ghost = this.ghosts.get(id);
    if (ghost === undefined) return;
    this.destroy(ghost);
    this.ghosts.delete(id);
  }

  /**
   * Record a snapshot. Stamped with the *local* clock — the server's `ts` is
   * advisory and unsynchronised, and using it would import the server's jitter.
   */
  handleSnap(msg: S2CSnap): void {
    const now = performance.now();
    for (const u of msg.u) {
      if (u.id === this.selfId) continue;
      const ghost = this.ghosts.get(u.id);
      // No `joined` yet (or already gone) — dropping is correct; the next
      // snapshot after the handshake will carry them.
      if (ghost === undefined) continue;
      ghost.buf.push(now, {
        p: [u.p[0], u.p[1], u.p[2]],
        l: [u.l[0], u.l[1]],
        y: u.y,
        a: u.a,
        c: u.c,
        v: u.v,
      });
    }
  }

  // -------------------------------------------------------------------------
  // per-frame
  // -------------------------------------------------------------------------

  /**
   * Pose every ghost for this frame.
   *
   * @param nowMs    `performance.now()` — the same clock `handleSnap` stamps with.
   * @param timeSec  Monotonic seconds, shared with the local player so animation
   *                 cycles stay in phase across the whole room.
   * @param cameraPos Camera world position, for LOD selection and tag fading.
   */
  update(nowMs: number, timeSec: number, cameraPos: Vector3): void {
    const dt = this.lastNow < 0 ? 0 : Math.min((nowMs - this.lastNow) / 1000, 0.1);
    this.lastNow = nowMs;
    const renderTime = nowMs - INTERP_DELAY_MS;

    this.order.length = 0;

    for (const ghost of this.ghosts.values()) {
      const sampled = ghost.buf.sample(renderTime);
      if (sampled === null) {
        // Never heard from — do not draw a skeleton at the origin.
        this.hide(ghost);
        continue;
      }
      this.buildPose(ghost, sampled, dt, timeSec);
      ghost.dist = ghost.position.distanceTo(cameraPos);
      ghost.posed = true;
      this.order.push(ghost);
    }

    // Nearest first: the top `LOD_FULL_COUNT` earn a skeleton.
    this.order.sort(byDistance);

    for (let i = 0; i < this.order.length; i++) {
      const ghost = this.order[i];
      if (ghost === undefined) continue;
      this.applyLod(ghost, i < LOD_FULL_COUNT);
      this.applyTag(ghost);
    }
  }

  private buildPose(ghost: Ghost, sampled: SampledState, dt: number, timeSec: number): void {
    const s = sampled.state;

    ghost.position.set(s.p[0], s.p[1], s.p[2]);
    ghost.heightM = s.p[1];

    // Impact pulse: the snapshot stream has no "landed" event, so we infer it
    // from the animation flipping out of the air and into the bounce cycle.
    const wasAirborne = ghost.prevAnim === ANIM_AIRBORNE || ghost.prevAnim === ANIM_FLAIL;
    if (wasAirborne && s.a === ANIM_BOUNCE) ghost.compression = 1;
    else ghost.compression = Math.max(0, ghost.compression - COMPRESSION_DECAY * dt);
    ghost.prevAnim = s.a;

    stickUpFromLean(s.l[0], s.l[1], s.y, ghost.stickDir);

    const pose = ghost.pose;
    pose.yaw = s.y;
    pose.compression = ghost.compression;
    pose.charge = s.c;
    pose.verticalVel = s.v;
    pose.anim = s.a;
    pose.timeSec = timeSec;

    ghost.opacity = sampled.staleMs > STALE_MS ? STALE_OPACITY : 1;
  }

  private applyLod(ghost: Ghost, full: boolean): void {
    if (full) {
      // Lazy: a ghost that never comes close never pays for 28 meshes.
      if (ghost.rig === null) {
        ghost.rig = this.createRig(ghost.info.color);
        // Tags are owned by the manager (they must not inherit the rig's
        // squash/stretch), so the rig is told explicitly not to draw one.
        ghost.rig.setNameTag(null);
        ghost.rigOpacity = 1;
        this.scene.add(ghost.rig.root);
      }
      const rig = ghost.rig;
      if (ghost.fallback.root.visible) ghost.fallback.root.visible = false;
      if (!rig.root.visible) rig.root.visible = true;
      if (ghost.rigOpacity !== ghost.opacity) {
        ghost.rigOpacity = ghost.opacity;
        rig.setOpacity(ghost.opacity);
      }
      rig.applyPose(ghost.pose);
    } else {
      if (ghost.rig !== null && ghost.rig.root.visible) ghost.rig.root.visible = false;
      if (!ghost.fallback.root.visible) ghost.fallback.root.visible = true;
      if (ghost.fallbackOpacity !== ghost.opacity) {
        ghost.fallbackOpacity = ghost.opacity;
        ghost.fallback.setOpacity(ghost.opacity);
      }
      ghost.fallback.applyPose(ghost.pose);
    }
    ghost.full = full;
  }

  private applyTag(ghost: Ghost): void {
    const sprite = ghost.tag.sprite;
    sprite.position.set(ghost.position.x, ghost.position.y + TAG_HEIGHT, ghost.position.z);
    const fade =
      ghost.dist <= TAG_FADE_NEAR
        ? 1
        : ghost.dist >= TAG_FADE_FAR
          ? 0
          : 1 - (ghost.dist - TAG_FADE_NEAR) / (TAG_FADE_FAR - TAG_FADE_NEAR);
    const o = fade * ghost.opacity;
    if (Math.abs(ghost.tagOpacity - o) > 0.004) {
      ghost.tagOpacity = o;
      ghost.tag.setOpacity(o);
    }
  }

  private hide(ghost: Ghost): void {
    ghost.full = false;
    if (ghost.rig !== null && ghost.rig.root.visible) ghost.rig.root.visible = false;
    if (ghost.fallback.root.visible) ghost.fallback.root.visible = false;
    if (ghost.tagOpacity !== 0) {
      ghost.tagOpacity = 0;
      ghost.tag.setOpacity(0);
    }
  }

  // -------------------------------------------------------------------------
  // queries
  // -------------------------------------------------------------------------

  /** Everyone with a known altitude, for the HUD leaderboard. Unordered. */
  getRoster(): RosterEntry[] {
    const out: RosterEntry[] = [];
    for (const ghost of this.ghosts.values()) {
      if (!ghost.buf.hasData) continue;
      out.push({
        id: ghost.info.id,
        name: ghost.info.name,
        color: ghost.info.color,
        heightM: ghost.heightM,
      });
    }
    return out;
  }

  /** How many remote players are known, posed or not. Excludes you. */
  count(): number {
    return this.ghosts.size;
  }

  /** True once at least one snapshot has been rendered for `id`. Diagnostics. */
  isPosed(id: number): boolean {
    return this.ghosts.get(id)?.posed === true;
  }

  /**
   * How many ghosts are currently drawn as full skeletons. Never exceeds
   * `LOD_FULL_COUNT`; the debug overlay uses it to prove LOD is engaging.
   */
  fullRigCount(): number {
    let n = 0;
    for (const ghost of this.ghosts.values()) if (ghost.full && ghost.posed) n++;
    return n;
  }

  /** Release every ghost. The manager stays usable — a later `welcome` refills it. */
  dispose(): void {
    this.clearGhosts();
    this.selfId = null;
    this.lastNow = -1;
  }

  // -------------------------------------------------------------------------
  // internals
  // -------------------------------------------------------------------------

  private spawn(info: PlayerInfo): Ghost {
    const fallback = createFallbackRig(info.color);
    fallback.setNameTag(null);
    fallback.root.visible = false;
    this.scene.add(fallback.root);

    const tag = createNameTag(info.name);
    tag.setOpacity(0);
    this.scene.add(tag.sprite);

    const position = new Vector3();
    const stickDir = new Vector3(0, 1, 0);
    const ghost: Ghost = {
      info,
      buf: new InterpBuffer(),
      rig: null,
      fallback,
      tag,
      position,
      stickDir,
      pose: {
        position,
        yaw: 0,
        stickDir,
        compression: 0,
        charge: 0,
        verticalVel: 0,
        anim: ANIM_BOUNCE,
        timeSec: 0,
      },
      posed: false,
      prevAnim: ANIM_BOUNCE,
      compression: 0,
      opacity: 1,
      rigOpacity: 1,
      fallbackOpacity: 1,
      tagOpacity: 0,
      dist: Infinity,
      heightM: 0,
      full: false,
    };
    this.ghosts.set(info.id, ghost);
    return ghost;
  }

  private destroy(ghost: Ghost): void {
    ghost.rig?.dispose();
    ghost.fallback.dispose();
    ghost.tag.dispose();
  }

  private clearGhosts(): void {
    for (const ghost of this.ghosts.values()) this.destroy(ghost);
    this.ghosts.clear();
    this.order.length = 0;
  }
}

function byDistance(a: Ghost, b: Ghost): number {
  return a.dist - b.dist;
}

